import { isAxiosError } from 'axios'
import { push } from 'connected-react-router'
import { call, put, select, takeEvery } from 'redux-saga/effects'

import { Role } from '@app/domain/iam/user.interface'
import type { AppAction, AppState } from '@app/state'
import * as Auth from '@app/state/authenticate/state'

import * as Intake from '../intake/state'

import * as Requests from './rest'
import * as User from './state'

import type {
  UpdatePasswordResponse,
  RetrieveAccountInfoResponse,
} from './interfaces'
import type { AxiosResponse} from 'axios';

function* handleUpdatePassword() {
  yield takeEvery(User.ActionType.UpdatePassword, updatePassword)
}

function* updatePassword(action: AppAction) {
  if (action.type === User.ActionType.UpdatePassword) {
    yield put(User.actions.passwordUpdateStarted())
    const appState: AppState = yield select((a) => a)
    try {
      const request = Requests.updatePassword(
        action.identifier,
        action.password,
        action.newPassword,
      )
      const response: AxiosResponse<UpdatePasswordResponse> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      if (response.data.status === 'success') {
        yield put(User.actions.passwordUpdateSucceeded())
      } else {
        yield put(
          User.actions.passwordUpdateFailed(
            response.data?.status ?? 'Error updating password',
          ),
        )
      }
    } catch (e) {
      if (isAxiosError(e)) {
        yield put(
          User.actions.passwordUpdateFailed(
            e.response?.data?.message ??
              'Unable to update password at this time',
          ),
        )
      }
    }
  }
}

function* handleGetAccountInfo() {
  yield takeEvery(User.ActionType.GetAccountInfo, retrieveAccountInfo)
}

function* retrieveAccountInfo(action: AppAction) {
  if (action.type === User.ActionType.GetAccountInfo) {
    const appState: AppState = yield select((a) => a)
    try {
      const request = Requests.retrieveAccountInfo() // will retrieve information on whoever is current logged in
      const response: AxiosResponse<RetrieveAccountInfoResponse> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      if (response.status === 200) {
        const userInfo = response.data
        yield put(
          User.actions.getAccountInfoSucceeded(
            userInfo.userId,
            userInfo.email,
            userInfo.givenName,
            userInfo.familyName,
            userInfo.role,
          ),
        )
      } else {
        yield put(
          User.actions.getAccountInfoFailed(
            'unable to retrieve account information (2)',
          ),
        )
      }
    } catch (e) {
      yield put(
        User.actions.getAccountInfoFailed(
          'unable to retrieve account information (1)',
        ),
      )
    }
  }
}

function* handleVerifyPIN() {
  yield takeEvery(User.ActionType.VerifyPIN, verifyPIN)
}

function* verifyPIN(action: AppAction) {
  if (action.type === User.ActionType.VerifyPIN) {
    const appState: AppState = yield select((a) => a)
    const request = Requests.verifyPIN(action.pin)
    const response: AxiosResponse<Requests.VerifyPINResponse> = yield call(
      request,
      appState.config.apiLocation,
      appState.auth.token,
    )
    if (response.status === 200) {
      if (response.data.status) {
        yield put(User.actions.verifyPINSucceeded(action.route))
        if (action.closeSurvey) {
          yield put(Intake.actions.closeSurvey())
        }
      } else {
        yield put(User.actions.verifyPINFailed('Incorrect PIN'))
      }
    } else {
      yield put(User.actions.verifyPINFailed('Unable to verify PIN'))
    }
  }
}

function* handleVerifyPINSucceeded() {
  yield takeEvery(User.ActionType.VerifyPINSucceeded, onVerifyPINSuccess)
}

function* onVerifyPINSuccess(action: AppAction) {
  if (action.type === User.ActionType.VerifyPINSucceeded) {
    yield put(Auth.actions.assumeRole(Role.Admin))
    yield put(push(action.route))
  }
}

export function* fetchAccountInfo() {
  const state = yield select((a) => a)
  if (Auth.queries.isAuthenticated(state.authenticate)) {
    yield put(User.actions.getAccountInfo())
  }
}

export function* handleClearPatientData() {
  yield takeEvery(User.ActionType.ClearPatientData, clearPatientData)
}

export function* clearPatientData(action: AppAction) {
  if (action.type === User.ActionType.ClearPatientData) {
    yield put(User.actions.clearPatientDataStarted())
    try {
      const appState: AppState = yield select((a) => a)
      const request = Requests.clearPatientData(
        action.passphrase,
        action.confirm,
      )
      const response: AxiosResponse<Requests.VerifyPINResponse> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      if (response.status === 200) {
        yield put(User.actions.clearPatientDataSuccess())
      } else {
        yield put(User.actions.clearPatientDataFailure('Unable to clear data'))
      }
    } catch (e) {
      yield put(User.actions.clearPatientDataFailure('Unable to clear data'))
    }
  }
}

export const sagas = {
  handleUpdatePassword,
  handleGetAccountInfo,
  handleVerifyPIN,
  handleVerifyPINSucceeded,
  handleClearPatientData,
}
